import { useEffect, useMemo, useState } from 'react';
import { Card, Grid, Text, Metric, SparkAreaChart } from '@tremor/react';
import { fetchInventoryHistory } from '../api/inventory_history';
import type { InventoryHistoryPoint, Role } from '../api/types';
import { t } from '../lib/i18n';
import ErrorRetry from './ErrorRetry';
import TableSkeleton from './TableSkeleton';

const mxn = (n: number | null) =>
  n === null
    ? '—'
    : n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });

type Window = '7d' | '30d' | '90d' | 'all';
const WINDOWS: { key: Window; label: string }[] = [
  { key: '7d',  label: '7d' },
  { key: '30d', label: '30d' },
  { key: '90d', label: '90d' },
  { key: 'all', label: 'Todo' },
];

export default function InventoryTrendStrip({ role, sid }: { role: Role; sid: number | null }) {
  const [data, setData] = useState<InventoryHistoryPoint[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [win, setWin] = useState<Window>('30d');

  const load = () => {
    let alive = true;
    setErr(null);
    fetchInventoryHistory()
      .then((d) => { if (alive) setData(d); })
      .catch((e: unknown) => {
        if (!alive) return;
        setErr(e instanceof Error ? e.message : String(e));
      });
    return () => { alive = false; };
  };
  useEffect(() => load(), []);

  // Filter by sid (or aggregate "all" if sid is null), then slice to the
  // chosen window. Window is applied AFTER per-store aggregation so "30d"
  // means the last 30 calendar days regardless of how many snapshots that
  // is.
  const filtered = useMemo(() => {
    if (!data) return null;
    const byStore = sid === null ? aggregateByDate(data) : data.filter((p) => p.sid === sid);
    if (win === 'all') return byStore;
    const days = win === '7d' ? 7 : win === '30d' ? 30 : 90;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    const cutoffISO = cutoff.toISOString().slice(0, 10);
    return byStore.filter((p) => p.snapshot_date >= cutoffISO);
  }, [data, sid, win]);

  if (err) return <ErrorRetry message={err} onRetry={load} />;
  if (!data) return <TableSkeleton rows={2} cols={4} />;
  if (!filtered || filtered.length === 0) {
    return (
      <Card>
        <Text className="text-tremor-content text-sm">Sin datos en esta ventana.</Text>
      </Card>
    );
  }

  const latest = filtered[filtered.length - 1];
  if (!latest) return <Card><Text>—</Text></Card>;

  const showCost = role !== 'staff';

  return (
    <div className="space-y-2">
      <div className="flex gap-1 items-center">
        <Text className="text-tremor-content text-xs mr-2">Ventana:</Text>
        {WINDOWS.map((w) => {
          const active = w.key === win;
          return (
            <button
              key={w.key}
              type="button"
              onClick={() => setWin(w.key)}
              className={
                'text-xs px-2 py-0.5 rounded-tremor-small border transition-colors ' +
                (active
                  ? 'bg-tremor-brand text-tremor-brand-inverted border-tremor-brand'
                  : 'bg-tremor-background text-tremor-content border-tremor-border hover:bg-tremor-background-muted')
              }
            >
              {w.label}
            </button>
          );
        })}
      </div>
      <Grid numItemsSm={2} numItemsLg={4} className="gap-2">
      {showCost && (
        <Card>
          <Text>{t.inventory.trend.totalCost}</Text>
          <Metric>{mxn(latest.total_inv_cost_mxn)}</Metric>
          <SparkAreaChart
            data={filtered}
            index="snapshot_date"
            categories={['total_inv_cost_mxn']}
            colors={['blue']}
            className="h-10 mt-2"
          />
        </Card>
      )}
      {showCost && (
        <Card>
          <Text>{t.inventory.trend.deadCost}</Text>
          <Metric>{mxn(latest.dead_cost_mxn)}</Metric>
          <SparkAreaChart
            data={filtered}
            index="snapshot_date"
            categories={['dead_cost_mxn']}
            colors={['red']}
            className="h-10 mt-2"
          />
        </Card>
      )}
      <Card>
        <Text>{t.inventory.trend.lowCount}</Text>
        <Metric>{latest.low_sku_count}</Metric>
        <SparkAreaChart
          data={filtered}
          index="snapshot_date"
          categories={['low_sku_count']}
          colors={['amber']}
          className="h-10 mt-2"
        />
      </Card>
      <Card>
        <Text>{t.inventory.trend.counted90}</Text>
        <Metric>{`${latest.counted_90d_pct.toFixed(1)}%`}</Metric>
        <Text className="text-xs text-tremor-content mt-1">
          {latest.counted_90d_pct < 1
            ? t.inventory.trend.noRecentCounts
            : `${latest.stale_sku_count.toLocaleString('es-MX')} ${t.inventory.trend.staleHint}`}
        </Text>
      </Card>
    </Grid>
    </div>
  );
}

/**
 * Aggregate per-store snapshots into per-date totals so a single sparkline
 * represents "all stores". Sums the count/cost fields; for the two ratio
 * fields (dead_pct, counted_90d_pct) we recompute from the running sums so
 * the result is the chain-wide weighted average rather than a per-store mean.
 */
function aggregateByDate(points: InventoryHistoryPoint[]): InventoryHistoryPoint[] {
  const byDate: Record<string, InventoryHistoryPoint> = {};
  for (const p of points) {
    const existing = byDate[p.snapshot_date];
    if (!existing) {
      byDate[p.snapshot_date] = { ...p, sid: 0 };
      continue;
    }
    const prevSkuCount = existing.sku_count;
    const prevCounted90Pct = existing.counted_90d_pct;
    existing.total_inv_cost_mxn =
      (existing.total_inv_cost_mxn ?? 0) + (p.total_inv_cost_mxn ?? 0);
    existing.dead_cost_mxn =
      (existing.dead_cost_mxn ?? 0) + (p.dead_cost_mxn ?? 0);
    existing.low_sku_count += p.low_sku_count;
    existing.a_class_count += p.a_class_count;
    existing.sku_count += p.sku_count;
    existing.stale_sku_count += p.stale_sku_count;
    const denom = existing.total_inv_cost_mxn ?? 0;
    existing.dead_pct = denom > 0 ? ((existing.dead_cost_mxn ?? 0) * 100) / denom : 0;
    existing.counted_90d_pct = existing.sku_count > 0
      ? (prevCounted90Pct * prevSkuCount + p.counted_90d_pct * p.sku_count) / existing.sku_count
      : 0;
  }
  return Object.values(byDate).sort((a, b) => a.snapshot_date.localeCompare(b.snapshot_date));
}
